import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { AITeamMember, HumanTeamMember, Task, TaskStatus } from '../types';
import { getTeamMembers, getHumanTeamMembers, getTasks, getTask, connectTasksWebSocket } from '../services/api';
import HumanMemberCard from '../components/HumanMemberCard';
import TaskCard from '../components/TaskCard';
import TaskDetail from '../components/TaskDetail';
import { useTheme } from '../context/ThemeContext';
import InfoModal from '../components/InfoModal';
import HistorySearchBar, { TimeRange, TIME_RANGE_TO_OPTIONS } from '../components/HistorySearchBar';

const Dashboard: React.FC = () => {
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [humanMembers, setHumanMembers] = useState<HumanTeamMember[]>([]);
  const [aiTeamMembers, setAiTeamMembers] = useState<AITeamMember[]>([]);
  const selectedHuman = searchParams.get('human');
  const taskIdParam = searchParams.get('task');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [aiMemberFilter, setAiMemberFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [timeRange, setTimeRange] = useState<TimeRange>('30');

  // Show the Tasks panel most-recent first (top to bottom). "Recent" = the latest activity on the
  // task, not just when it was created, so a task that just completed, failed, or moved to review
  // rises to the top. We take the newest of its timestamps and sort descending.
  const sortedTasks = useMemo(() => {
    const recency = (t: Task): number => {
      const stamps = [t.completed_at, t.updated_at, t.started_at, t.created_at]
        .filter(Boolean)
        .map((s) => new Date(s as string).getTime())
        .filter((n) => !isNaN(n));
      return stamps.length ? Math.max(...stamps) : 0;
    };
    return [...tasks].sort((a, b) => recency(b) - recency(a));
  }, [tasks]);

  useEffect(() => {
    loadHumanMembers();
    loadAiTeamMembers();
  }, []);

  const loadHumanMembers = async () => {
    try {
      const members = await getHumanTeamMembers();
      setHumanMembers(members);
      setLoading(false);
    } catch (error) {
      console.error('Error loading human members:', error);
      setLoading(false);
    }
  };

  const loadAiTeamMembers = async () => {
    try {
      const members = await getTeamMembers();
      setAiTeamMembers(members);
    } catch (error) {
      console.error('Error loading AI team members:', error);
    }
  };

  // Load task from URL parameter if present
  useEffect(() => {
    const loadTaskFromParam = async () => {
      if (taskIdParam) {
        try {
          const task = await getTask(parseInt(taskIdParam, 10));
          setSelectedTask(task);
          // Also set the human filter to show context
          if (task.created_by && !selectedHuman) {
            setSearchParams({ human: task.created_by, task: taskIdParam });
          }
        } catch (error) {
          console.error('Failed to load task:', error);
        }
      }
    };
    loadTaskFromParam();
  }, [taskIdParam]);

  useEffect(() => {
    if (selectedHuman) {
      loadTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHuman, statusFilter, aiMemberFilter, search, timeRange]);

  // Live: refetch when any task changes anywhere (incl. agent-created tasks via
  // manage_task.py) so the dashboard reflects it immediately, no manual refresh.
  useEffect(() => {
    const sub = connectTasksWebSocket(() => {
      if (selectedHuman) loadTasks();
    });
    return () => sub.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHuman, statusFilter, aiMemberFilter, search, timeRange]);

  const loadTasks = async () => {
    if (!selectedHuman) return;

    try {
      const filter = statusFilter === 'all' ? undefined : statusFilter;
      const aiMember = aiMemberFilter || undefined;
      // Note: getTasks takes (aiTeamMember, status, featureId, createdBy)
      // We're now filtering by createdBy (human) as primary, and optionally by AI member
      const taskList = await getTasks(aiMember, filter, undefined, selectedHuman, undefined, {
        search: search.trim() || undefined,
        ...TIME_RANGE_TO_OPTIONS(timeRange),
      });
      setTasks(taskList);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const handleTaskUpdate = () => {
    loadTasks();
    loadHumanMembers();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: theme.background }}>
      <div className="dashboard-layout" style={{ display: 'flex', flexDirection: 'row' }}>
        {/* Sidebar - Human Team Members */}
        <div className="sidebar" style={{
          width: '300px',
          minWidth: '300px',
          backgroundColor: theme.surface,
          borderRight: `1px solid ${theme.border}`,
          padding: '16px',
          height: 'calc(100vh - 56px)',
          overflowY: 'auto',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: theme.text }}>Tasks</h2>
              <InfoModal
                title="Tasks"
                size="sm"
                body={[
                  "The dev-server task board: structured work you send off to an AI team member to run in the background, organized by who created it and which feature it touches.",
                  "Pick a person to filter their tasks; open any task to read the AI's output, then respond or mark it complete. Tasks move through Queued → In Progress → Ready for Review.",
                  "For live, fire-and-watch one-offs and frontend QA, use Quick Tasks instead.",
                ]}
              />
            </div>
            {selectedHuman && (
              <button
                onClick={() => setSearchParams({})}
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Clear
              </button>
            )}
          </div>
          {humanMembers.map((member) => (
            <HumanMemberCard
              key={member.name}
              member={member}
              isSelected={selectedHuman === member.name}
              onClick={() => setSearchParams({ human: member.name })}
            />
          ))}
          {humanMembers.length === 0 && (
            <p style={{ color: theme.textSecondary, textAlign: 'center', padding: '20px' }}>
              No team members yet. Create a task to see members here.
            </p>
          )}
        </div>

        {/* Main Content - Tasks */}
        <div className="main-content" style={{ flex: 1, padding: '16px', overflowY: 'auto', height: 'calc(100vh - 56px)' }}>
          {selectedHuman ? (
            <>
              {/* Header with title */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
              }}>
                <h2 style={{ margin: 0, color: theme.text }}>
                  Tasks by {selectedHuman}
                </h2>
              </div>

              {/* Search + time range */}
              <div style={{ marginBottom: '16px' }}>
                <HistorySearchBar
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search title or description..."
                  timeRange={timeRange}
                  onTimeRangeChange={setTimeRange}
                />
              </div>

              {/* Filters row */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                flexWrap: 'wrap',
                gap: '12px',
              }}>
                {/* AI Team Member filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '14px', color: theme.textSecondary }}>AI Team Member:</label>
                  <select
                    value={aiMemberFilter}
                    onChange={(e) => setAiMemberFilter(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: theme.surface,
                      color: theme.text,
                      minWidth: '150px',
                    }}
                  >
                    <option value="">All AI Members</option>
                    {aiTeamMembers.map((member) => (
                      <option key={member.name} value={member.name}>{member.name}</option>
                    ))}
                  </select>
                  {aiMemberFilter && (
                    <button
                      onClick={() => setAiMemberFilter('')}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: 'transparent',
                        border: `1px solid ${theme.border}`,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: theme.textSecondary,
                      }}
                      title="Clear filter"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Status filters */}
                <div className="status-filters" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setStatusFilter('all')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: statusFilter === 'all' ? '#3b82f6' : '#f3f4f6',
                      color: statusFilter === 'all' ? '#fff' : '#6b7280',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setStatusFilter(TaskStatus.QUEUED)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: statusFilter === TaskStatus.QUEUED ? '#f59e0b' : '#f3f4f6',
                      color: statusFilter === TaskStatus.QUEUED ? '#fff' : '#6b7280',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Queued
                  </button>
                  <button
                    onClick={() => setStatusFilter(TaskStatus.IN_PROGRESS)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: statusFilter === TaskStatus.IN_PROGRESS ? '#3b82f6' : '#f3f4f6',
                      color: statusFilter === TaskStatus.IN_PROGRESS ? '#fff' : '#6b7280',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    In Progress
                  </button>
                  <button
                    onClick={() => setStatusFilter(TaskStatus.READY_FOR_REVIEW)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: statusFilter === TaskStatus.READY_FOR_REVIEW ? '#8b5cf6' : '#f3f4f6',
                      color: statusFilter === TaskStatus.READY_FOR_REVIEW ? '#fff' : '#6b7280',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Ready for Review
                  </button>
                  <button
                    onClick={() => setStatusFilter(TaskStatus.COMPLETED)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: statusFilter === TaskStatus.COMPLETED ? '#10b981' : '#f3f4f6',
                      color: statusFilter === TaskStatus.COMPLETED ? '#fff' : '#6b7280',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Completed
                  </button>
                  <button
                    onClick={() => setStatusFilter(TaskStatus.FAILED)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: statusFilter === TaskStatus.FAILED ? '#ef4444' : '#f3f4f6',
                      color: statusFilter === TaskStatus.FAILED ? '#fff' : '#6b7280',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Failed
                  </button>
                </div>
              </div>

              {tasks.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#6b7280',
                }}>
                  <p style={{ fontSize: '18px', marginBottom: '8px' }}>No tasks found</p>
                  <p style={{ fontSize: '14px' }}>
                    {statusFilter === 'all'
                      ? 'This team member has no tasks yet.'
                      : `No ${statusFilter.replace('_', ' ')} tasks.`}
                  </p>
                </div>
              ) : (
                <div>
                  {sortedTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onClick={() => setSelectedTask(task)}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: theme.textSecondary,
            }}>
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ fontSize: '24px', marginBottom: '8px', color: theme.text }}>Select a Team Member</h3>
                <p>Choose a team member from the sidebar to view their tasks</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetail
          task={selectedTask}
          onClose={() => {
            setSelectedTask(null);
            // Clear task param from URL when closing
            if (taskIdParam) {
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('task');
              setSearchParams(newParams);
            }
          }}
          onTaskUpdate={handleTaskUpdate}
        />
      )}

      {/* Floating Action Button - Create Task */}
      <Link
        to={selectedHuman ? `/create?created_by=${encodeURIComponent(selectedHuman)}` : '/create'}
        style={{
          position: 'fixed',
          bottom: '32px',
          right: '32px',
          width: '64px',
          height: '64px',
          backgroundColor: '#3b82f6',
          color: '#fff',
          borderRadius: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '28px',
          textDecoration: 'none',
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
          transition: 'all 0.2s',
          zIndex: 999,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
        }}
        title={selectedHuman ? `Create task as ${selectedHuman}` : 'Create new task'}
      >
        +
      </Link>

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .dashboard-layout {
            flex-direction: column !important;
          }
          .sidebar {
            width: 100% !important;
            min-width: 100% !important;
            height: auto !important;
            max-height: 40vh !important;
            border-right: none !important;
            border-bottom: 1px solid var(--border-color, #e5e7eb) !important;
          }
          .main-content {
            height: auto !important;
            min-height: 50vh !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
